<template>
  <section>
    <h2>AI 自動校準</h2>
    <b-button
      variant="outline-secondary"
      :disabled="isLoadingCalibration || isSubmittingCalibration"
      @click="loadCalibrationData"
    >
      <b-icon icon="arrow-clockwise" aria-hidden="true" />
      更新校準資料
    </b-button>
    <p v-if="isLoadingCalibration">讀取校準資料中…</p>
    <b-alert :show="Boolean(calibrationLoadError)" variant="danger">
      {{ calibrationLoadError }}
    </b-alert>
    <p>題數：{{ itemCount }}</p>
    <b-form-group
      label="最大校準輪數（1–10）"
      description="某一輪達標就提前停止並進入 10 次驗證；未達標會依不一致題目修正判斷標準再試一輪。"
    >
      <b-form-input
        v-model.number="maxRounds"
        type="number"
        min="1"
        max="10"
        step="1"
        :disabled="isSubmittingCalibration || calibration.status === 'running'"
      />
    </b-form-group>
    <p v-if="itemCount > 0">預估 AI 呼叫次數上限：{{ estimatedCalls }}</p>
    <b-form-group label="額外判斷說明（選填）">
      <b-form-textarea
        v-model="extraInstruction"
        maxlength="2000"
        rows="3"
        :disabled="isSubmittingCalibration || calibration.status === 'running'"
      />
    </b-form-group>
    <b-form-group
      label="驗證時的 AI temperature（0–1）"
      description="10 次驗證使用此值，數值越高越能看出結果是否穩定；第 1 輪編碼固定為 0。"
    >
      <b-form-input
        v-model.number="verifyTemperature"
        type="number"
        min="0"
        max="1"
        step="0.1"
        :disabled="isSubmittingCalibration || calibration.status === 'running'"
      />
    </b-form-group>
    <b-button
      v-if="calibration.status !== 'not_passed'"
      variant="primary"
      :disabled="
        isLoadingCalibration ||
        isSubmittingCalibration ||
        itemCount <= 0 ||
        Boolean(calibrationLoadError) ||
        calibration.status === 'running'
      "
      @click="startCalibration()"
    >
      <b-icon icon="play-fill" aria-hidden="true" />
      {{ isSubmittingCalibration ? "送出中…" : "開始 AI 校準" }}
    </b-button>
    <b-button
      v-if="calibration.status === 'running'"
      variant="outline-danger"
      :disabled="isSubmittingCalibration || isLoadingCalibration"
      @click="cancelCalibration"
    >
      <b-icon icon="stop-fill" aria-hidden="true" />
      取消校準
    </b-button>

    <b-button
      v-if="calibration.status === 'not_passed'"
      variant="primary"
      :disabled="
        isSubmittingCalibration ||
        isLoadingCalibration ||
        Boolean(calibrationLoadError)
      "
      @click="startCalibration(calibration.id)"
    >
      <b-icon icon="arrow-repeat" aria-hidden="true" />
      重新校準
    </b-button>

    <b-alert :show="Boolean(calibrationRequestError)" variant="danger">
      {{ calibrationRequestError }}
    </b-alert>

    <p>校準狀態：{{ statusText }}</p>
    <p>目前階段：{{ phaseText }}</p>

    <section v-if="calibration.id && calibration.params" class="mb-3">
      <h3>校準紀錄設定</h3>
      <p>校準 ID：{{ calibration.id }}</p>
      <p>統計方法：{{ calibration.params.method }}</p>
      <p>
        人類結果 ID：
        {{ (calibration.params.humanResultIds || []).join("、") }}
      </p>
      <p>最大輪數：{{ calibration.params.maxRounds }}</p>
      <p v-if="calibration.params.verifyTemperature != null">
        驗證 temperature：{{ calibration.params.verifyTemperature }}
      </p>

      <template v-if="calibration.params.thresholds">
        <p v-if="calibration.params.method === 'kappa'">
          門檻：κ ≥ {{ calibration.params.thresholds.minKappa }}
        </p>
        <p v-else-if="calibration.params.method === 'pearson'">
          門檻：r ≥ {{ calibration.params.thresholds.minR }}， p &lt;
          {{ calibration.params.thresholds.maxP }}
        </p>
      </template>

      <p v-if="calibration.params.extraInstruction">
        額外判斷說明：{{ calibration.params.extraInstruction }}
      </p>
      <b-alert :show="calibrationSettingsDiffer" variant="warning">
        此校準紀錄的設定與目前比較設定不同，以下結果屬於這筆校準紀錄。
      </b-alert>
    </section>
    <b-alert :show="calibration.status === 'failed'" variant="danger">
      {{ calibration.error || "校準失敗，請稍後重試。" }}
    </b-alert>

    <div
      v-if="calibration.status === 'running' && calibration.progress"
      class="mb-3"
    >
      <p>
        已結束 {{ progressFinishedCount }} /
        {{ calibration.progress.total }} 個任務
      </p>

      <b-progress
        :value="progressFinishedCount"
        :max="calibration.progress.total"
        class="mb-3"
      />

      <p>
        成功 {{ calibration.progress.done }}、 失敗
        {{ calibration.progress.failed }}、 等待
        {{ calibration.progress.pending }}、 執行中
        {{ calibration.progress.running }}
      </p>
    </div>

    <b-alert show variant="warning">
      校準針對同一批題目最佳化，可能過度擬合。
      通過代表在這批題目上穩定，不代表能推廣到其他題目。
    </b-alert>

    <div v-for="round in calibration.rounds" :key="round.no" class="mt-4">
      <h3>
        第 {{ round.no }} 輪
        <b-badge v-if="round.no === calibration.bestRoundNo" variant="info">
          最佳輪
        </b-badge>
      </h3>
      <p>{{ formatStat(round.stat) }}</p>
      <p>
        本輪狀態：
        {{
          round.passed === null ? "尚未判定" : round.passed ? "達標" : "未達標"
        }}
      </p>
      <p>不一致題號：{{ round.mismatchNos.join("、") || "無" }}</p>
      <p>失敗題號：{{ round.failedNos.join("、") || "無" }}</p>

      <b-form-group
        label="判斷標準 prompt"
        :label-for="'qa-round-prompt-' + round.no"
      >
        <b-form-textarea
          :id="'qa-round-prompt-' + round.no"
          :value="round.prompt"
          readonly
          rows="4"
        />
      </b-form-group>
    </div>
    <div v-if="calibration.verify" class="mt-4">
      <h3>10 次驗證</h3>

      <b-table :items="verifyRows" :fields="verifyFields" responsive />
      <p class="mt-3">通過次數：{{ calibration.verify.passCount }} / 10</p>

      <p>平均值：{{ formatValue(calibration.verify.mean) }}</p>

      <p>最小值：{{ formatValue(calibration.verify.min) }}</p>

      <b-alert :show="calibration.verify.passed === true" variant="success">
        10 次驗證全部達標。
      </b-alert>

      <b-alert :show="calibration.verify.passed === false" variant="warning">
        驗證未通過，不可存入應用庫。
      </b-alert>
    </div>

    <div v-if="calibration.status === 'passed'" class="mt-4">
      <h3>存入應用庫</h3>

      <b-form-group label="最終判斷標準" label-for="qa-final-prompt">
        <b-form-textarea
          id="qa-final-prompt"
          :value="calibration.verify && calibration.verify.prompt"
          readonly
          rows="4"
        />
      </b-form-group>

      <b-form-group label="應用名稱" label-for="qa-application-name">
        <b-form-input
          id="qa-application-name"
          v-model.trim="applicationName"
          maxlength="128"
          :disabled="isSavingApplication || Boolean(savedApplicationId)"
        />
      </b-form-group>

      <b-form-checkbox
        v-model="saveVerifyResults"
        :disabled="isSavingApplication || Boolean(savedApplicationId)"
        class="mb-3"
      >
        同時將 10 次驗證的 AI 編碼結果存入結果庫
      </b-form-checkbox>

      <b-alert :show="Boolean(applicationError)" variant="warning">
        {{ applicationError }}
      </b-alert>

      <b-button
        variant="primary"
        :disabled="
          Boolean(applicationError) ||
          isSavingApplication ||
          Boolean(savedApplicationId)
        "
        @click="saveApplication"
      >
        <b-icon icon="check-circle" aria-hidden="true" />
        {{ isSavingApplication ? "儲存中…" : "存入應用庫" }}
      </b-button>
      <b-alert :show="Boolean(applicationSaveError)" variant="danger">
        {{ applicationSaveError }}
      </b-alert>
      <b-alert :show="Boolean(savedApplicationId)" variant="success">
        應用已儲存，ID：{{ savedApplicationId }}
      </b-alert>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import { pollUntil } from "@/utils/jobPolling";
export default {
  name: "QACalibration",
  props: {
    context: { type: Object, required: true },
  },

  data() {
    return {
      itemCount: 0,
      isLoadingCalibration: false,
      calibrationLoadError: "",
      maxRounds: 5,
      applicationName: "",
      saveVerifyResults: false,
      verifyFields: [
        { key: "runNo", label: "驗證次數" },
        { key: "statText", label: "統計結果" },
        { key: "statusText", label: "是否達標" },
      ],
      calibration: {
        status: "idle",
        phase: null,
        error: null,
        rounds: [],
        progress: null,
        verify: null,
      },
      extraInstruction: "",
      verifyTemperature: 0.7,
      isSubmittingCalibration: false,
      calibrationRequestError: "",
      isSavingApplication: false,
      applicationSaveError: "",
      savedApplicationId: null,
    };
  },

  computed: {
    statusText() {
      const names = {
        running: "進行中",
        passed: "驗證通過",
        not_passed: "未達標",
        failed: "發生錯誤",
        cancelled: "已取消",
        idle: "尚未開始",
      };

      return names[this.calibration.status] || "未知狀態";
    },

    phaseText() {
      if (this.calibration.status === "idle") return "尚未開始";
      const names = {
        generate: "產生判斷標準",
        coding: "本輪 AI 編碼",
        verifying: "10 次驗證",
        finished: "已結束",
      };

      return names[this.calibration.phase] || "未知階段";
    },

    estimatedCalls() {
      return this.maxRounds * (1 + this.itemCount) + 10 * this.itemCount;
    },

    verifyRows() {
      const verify = this.calibration.verify;
      if (!verify) return [];

      return verify.runs.map((run, index) => ({
        runNo: index + 1,
        statText: this.formatStat(run.stat),
        statusText:
          run.passed === true
            ? "達標"
            : run.passed === false
            ? "未達標"
            : "尚未判定",
        _rowVariant: run.passed === false ? "danger" : null,
      }));
    },

    applicationError() {
      if (this.isLoadingCalibration || this.isSubmittingCalibration) {
        return "請等待校準資料讀取或操作完成";
      }
      if (
        !this.calibration.id ||
        this.calibration.verify?.passed !== true ||
        this.calibration.verify?.passCount !== 10
      ) {
        return "校準必須通過十次驗證才能儲存";
      }

      if (this.calibration.status !== "passed") {
        return "校準尚未通過，不可存入應用庫";
      }

      const verify = this.calibration.verify;

      if (!verify || !verify.prompt || !verify.prompt.trim()) {
        return "沒有可儲存的判斷標準";
      }

      if (!this.applicationName.trim()) {
        return "請填寫應用名稱";
      }

      if (this.applicationName.trim().length > 128) {
        return "應用名稱不可超過 128 個字元";
      }

      return "";
    },

    progressFinishedCount() {
      const progress = this.calibration.progress;

      if (!progress) return 0;

      return progress.done + progress.failed;
    },

    calibrationSettingsDiffer() {
      const params = this.calibration.params;
      if (!this.calibration.id || !params) return false;

      if (
        this.calibration.criterionId !== this.context.criterionId ||
        params.method !== this.context.method ||
        JSON.stringify(params.humanResultIds) !==
          JSON.stringify(this.context.humanResultIds)
      )
        return true;

      const keys = params.method === "kappa" ? ["minKappa"] : ["minR", "maxP"];
      return keys.some(
        (key) => params.thresholds?.[key] !== this.context.thresholds?.[key],
      );
    },
  },

  watch: {
    context: {
      immediate: true,
      handler() {
        this.loadCalibrationData();
      },
    },
  },
  beforeDestroy() {
    this._loadVersion = (this._loadVersion || 0) + 1;
    if (this._calibrationPoll) this._calibrationPoll.cancel();
  },

  methods: {
    async loadCalibrationData() {
      if (this.isSavingApplication) return;
      if (this.isSubmittingCalibration) return;
      this.calibrationRequestError = "";
      if (this._calibrationPoll) this._calibrationPoll.cancel();

      const version = (this._loadVersion = (this._loadVersion || 0) + 1);
      this.isLoadingCalibration = true;
      this.calibrationLoadError = "";
      this.itemCount = 0;
      this.savedApplicationId = null;
      this.applicationSaveError = "";
      this.applicationName = "";
      this.saveVerifyResults = false;
      this.calibration = {
        status: "idle",
        phase: null,
        error: null,
        rounds: [],
        progress: null,
        verify: null,
      };

      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const url = base.replace(/\/+$/, "");
        const criterionId = this.context.criterionId;
        const criterion = await axios.get(
          `${url}/api/qa/criteria/${criterionId}`,
        );
        if (version !== this._loadVersion) return;
        this.itemCount = criterion.data.items.length;

        const list = await axios.get(`${url}/api/qa/calibrations`, {
          params: { criterionId },
        });
        if (version !== this._loadVersion) return;

        if (list.data.length > 0) {
          const detail = await axios.get(
            `${url}/api/qa/calibrations/${encodeURIComponent(list.data[0].id)}`,
          );
          if (version !== this._loadVersion) return;
          this.calibration = detail.data;
          if (detail.data.status === "running") {
            this.pollCalibration(url, detail.data.id, version);
          }
        }
      } catch (error) {
        if (version === this._loadVersion) {
          this.calibrationLoadError =
            error.response?.data?.message ||
            error.message ||
            "讀取校準資料失敗";
        }
      } finally {
        if (version === this._loadVersion) this.isLoadingCalibration = false;
      }
    },
    async startCalibration(previousCalibrationId = null) {
      if (this.isSavingApplication) return;
      if (
        this.isLoadingCalibration ||
        this.isSubmittingCalibration ||
        this.calibration.status === "running" ||
        this.itemCount <= 0 ||
        this.calibrationLoadError
      )
        return;

      if (
        previousCalibrationId !== null &&
        (this.calibration.status !== "not_passed" ||
          previousCalibrationId !== this.calibration.id)
      )
        return;

      const version = this._loadVersion;
      this.isSubmittingCalibration = true;
      this.calibrationRequestError = "";
      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          `最多 ${this.maxRounds} 輪，預估最多 ${this.estimatedCalls} 次 AI 呼叫，會產生費用。確定開始？`,
          { title: "開始校準", okTitle: "開始", cancelTitle: "取消" },
        );
        if (!confirmed || version !== this._loadVersion) return;

        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const url = base.replace(/\/+$/, "");
        const body = {
          criterionId: this.context.criterionId,
          humanResultIds: [...this.context.humanResultIds],
          method: this.context.method,
          thresholds:
            this.context.method === "kappa"
              ? { minKappa: this.context.thresholds.minKappa }
              : {
                  minR: this.context.thresholds.minR,
                  maxP: this.context.thresholds.maxP,
                },
          maxRounds: this.maxRounds,
          verifyTemperature: this.verifyTemperature,
          extraInstruction: this.extraInstruction,
          previousCalibrationId,
        };
        const response = await axios.post(`${url}/api/qa/calibrations`, body);
        if (version !== this._loadVersion) return;

        this.calibration = {
          id: response.data.calibrationId,
          status: "running",
          phase: "generate",
          params: body,
          rounds: [],
          verify: null,
          progress: null,
          error: null,
        };
        this.applicationName = "";
        this.saveVerifyResults = false;
        this.savedApplicationId = null;
        this.applicationSaveError = "";
        this.pollCalibration(url, response.data.calibrationId, version);
      } catch (error) {
        if (version === this._loadVersion) {
          this.calibrationRequestError =
            error.response?.data?.message ||
            error.message ||
            "建立校準失敗，請先更新校準資料確認是否已建立";
        }
      } finally {
        this.isSubmittingCalibration = false;
      }
    },
    async cancelCalibration() {
      if (this.isSavingApplication) return;
      if (this.isSubmittingCalibration || this.calibration.status !== "running")
        return;
      const version = this._loadVersion;
      const id = this.calibration.id;
      this.isSubmittingCalibration = true;
      this.calibrationRequestError = "";

      try {
        const confirmed = await this.$bvModal.msgBoxConfirm(
          "確定取消校準？已執行的 AI 呼叫費用不會因此退回。",
          { title: "取消校準", okTitle: "取消校準", cancelTitle: "繼續執行" },
        );
        if (!confirmed || version !== this._loadVersion) return;

        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const url = base.replace(/\/+$/, "");
        if (this._calibrationPoll) this._calibrationPoll.cancel();

        await axios.post(
          `${url}/api/qa/calibrations/${encodeURIComponent(id)}/cancel`,
        );
        if (version !== this._loadVersion) return;
        this.calibration = { ...this.calibration, status: "cancelled" };
      } catch (error) {
        if (version === this._loadVersion) {
          this.calibrationRequestError =
            error.response?.data?.message ||
            error.message ||
            "取消失敗，請更新校準資料確認狀態";
        }
      } finally {
        this.isSubmittingCalibration = false;
      }
    },
    async saveApplication() {
      if (
        this.applicationError ||
        this.isSavingApplication ||
        this.savedApplicationId
      )
        return;

      const version = this._loadVersion;
      this.isSavingApplication = true;
      this.applicationSaveError = "";
      try {
        const base = process.env.VUE_APP_API_BASE;
        if (!base) throw new Error("尚未設定 API 網址");
        const response = await axios.post(
          `${base.replace(/\/+$/, "")}/api/qa/applications`,
          {
            criterionId: this.calibration.criterionId,
            calibrationId: this.calibration.id,
            name: this.applicationName.trim(),
            note: null,
            saveVerifyResults: this.saveVerifyResults,
          },
        );
        if (version === this._loadVersion) {
          this.savedApplicationId = response.data.id;
        }
      } catch (error) {
        if (version === this._loadVersion) {
          this.applicationSaveError =
            error.response?.data?.message || error.message || "儲存應用失敗";
        }
      } finally {
        this.isSavingApplication = false;
      }
    },

    formatStat(stat) {
      if (!stat) return "尚無統計結果";

      if (Object.prototype.hasOwnProperty.call(stat, "kappa")) {
        return stat.kappa === null ? "Kappa 無法計算" : `Kappa：${stat.kappa}`;
      }

      if (Object.prototype.hasOwnProperty.call(stat, "r")) {
        return `r：${stat.r ?? "無法計算"}，p：${stat.p ?? "無法計算"}`;
      }

      return "未知統計格式";
    },
    formatValue(value) {
      return Number.isFinite(value) ? value.toFixed(3) : "尚無數值";
    },

    pollCalibration(url, id, version) {
      if (this._calibrationPoll) this._calibrationPoll.cancel();

      const handle = pollUntil(
        () =>
          axios
            .get(`${url}/api/qa/calibrations/${encodeURIComponent(id)}`)
            .then((response) => response.data),
        (data) => data.status !== "running",
        {
          intervalMs: 2000,
          onUpdate: (data) => {
            if (version === this._loadVersion) this.calibration = data;
          },
        },
      );
      this._calibrationPoll = handle;
      handle.promise
        .catch((error) => {
          if (version === this._loadVersion) {
            this.calibrationLoadError =
              error.response?.data?.message ||
              error.message ||
              "進度讀取中斷，請更新校準資料";
          }
        })
        .finally(() => {
          if (this._calibrationPoll === handle) this._calibrationPoll = null;
        });
    },
  },
};
</script>
